import { supabase } from '@/lib/supabase'
import type {
  LeaseTableRow,
  LeaseFormData,
  LeaseFilters,
  ContractAircraft,
  UnitType,
  FuelType,
  FuelPriceType,
} from '@/types/leases'
import { applyRecordStatusFilter } from '@/lib/api/helpers/apply-record-status-filter'
import { restoreRecord } from '@/lib/api/helpers/restore-record'

const LEASE_SELECT = `
  id,
  organization_id,
  location_id,
  contract_number,
  client_id,
  contact_id,
  unit_id,
  type,
  start_date,
  end_date,
  term_months,
  monthly_rate,
  rate_basis,
  security_deposit,
  escalation_type,
  escalation_pct,
  escalation_date,
  auto_renew,
  renewal_notice_days,
  renewal_status,
  renewal_contacted_date,
  allow_transient_sublease,
  transient_revenue_share_pct,
  placement_guarantee,
  requires_unblocked_access,
  status,
  termination_reason,
  termination_date,
  cancellation_reason,
  document_url,
  signed_at,
  notes,
  fuel_type,
  fuel_commitment_gallons_monthly,
  fuel_commitment_gallons_annual,
  fuel_price_per_gallon,
  fuel_price_type,
  fuel_price_differential,
  created_at,
  updated_at,
  deleted_at,
  client:client_id (
    client_type,
    first_name,
    last_name,
    company_name
  ),
  contact:contact_id (
    first_name,
    last_name
  ),
  unit:unit_id (
    unit_code,
    type,
    area_id,
    area:area_id (
      name
    )
  ),
  location:location_id (
    name
  ),
  contract_aircraft!lease_contract_id (
    aircraft_id,
    is_primary,
    status,
    aircraft:aircraft_id (
      tail_number,
      aircraft_types:aircraft_type_id (
        manufacturer,
        model
      )
    )
  )
`

/**
 * Transform a raw Supabase row into a flat LeaseTableRow
 */
function mapToLeaseTableRow(row: Record<string, unknown>): LeaseTableRow {
  const client = row.client as {
    client_type: string
    first_name: string | null
    last_name: string | null
    company_name: string | null
  } | null
  const clientName = client?.client_type === 'individual'
    ? [client.first_name, client.last_name].filter(Boolean).join(' ') || null
    : client?.company_name ?? null
  const contact = row.contact as { first_name: string; last_name: string } | null
  const unit = row.unit as {
    unit_code: string
    type: UnitType | null
    area_id: string | null
    area: { name: string } | null
  } | null
  const location = row.location as { name: string } | null

  // Extract primary aircraft from nested join
  const contractAircraftRows = (row.contract_aircraft ?? []) as Array<{
    aircraft_id: string
    is_primary: boolean | null
    status: string
    aircraft: {
      tail_number: string
      aircraft_types: { manufacturer: string; model: string } | null
    } | null
  }>
  const primaryAircraft = contractAircraftRows.find(
    (ca) => ca.is_primary && ca.status === 'active'
  ) ?? contractAircraftRows.find((ca) => ca.status === 'active') ?? null

  const primaryAircraftTail = primaryAircraft?.aircraft?.tail_number ?? null
  const primaryAircraftType = primaryAircraft?.aircraft?.aircraft_types
    ? `${primaryAircraft.aircraft.aircraft_types.manufacturer} ${primaryAircraft.aircraft.aircraft_types.model}`
    : null

  const contactName = contact
    ? `${contact.first_name} ${contact.last_name}`.trim()
    : null

  return {
    id: row.id as string,
    organization_id: row.organization_id as string,
    location_id: row.location_id as string,
    contract_number: row.contract_number as string,
    client_id: row.client_id as string,
    client_name: clientName,
    contact_id: row.contact_id as string | null,
    contact_name: contactName,
    unit_id: row.unit_id as string,
    unit_code: unit?.unit_code ?? null,
    unit_type: unit?.type ?? null,
    building_id: unit?.area_id ?? null,
    building_name: unit?.area?.name ?? null,
    location_name: location?.name ?? null,
    type: row.type as LeaseTableRow['type'],
    start_date: row.start_date as string,
    end_date: row.end_date as string | null,
    term_months: row.term_months as number | null,
    monthly_rate: row.monthly_rate as number,
    rate_basis: row.rate_basis as LeaseTableRow['rate_basis'],
    security_deposit: row.security_deposit as number | null,
    escalation_type: row.escalation_type as LeaseTableRow['escalation_type'],
    escalation_pct: row.escalation_pct as number | null,
    escalation_date: row.escalation_date as string | null,
    auto_renew: (row.auto_renew as boolean) ?? false,
    renewal_notice_days: row.renewal_notice_days as number | null,
    renewal_status: row.renewal_status as LeaseTableRow['renewal_status'],
    renewal_contacted_date: row.renewal_contacted_date as string | null,
    allow_transient_sublease: (row.allow_transient_sublease as boolean) ?? false,
    transient_revenue_share_pct: row.transient_revenue_share_pct as number | null,
    placement_guarantee: (row.placement_guarantee as boolean) ?? true,
    requires_unblocked_access: (row.requires_unblocked_access as boolean) ?? false,
    status: row.status as LeaseTableRow['status'],
    termination_reason: row.termination_reason as string | null,
    termination_date: row.termination_date as string | null,
    cancellation_reason: row.cancellation_reason as string | null,
    document_url: row.document_url as string | null,
    signed_at: row.signed_at as string | null,
    notes: row.notes as string | null,
    fuel_type: (row.fuel_type as FuelType) ?? null,
    fuel_commitment_gallons_monthly: (row.fuel_commitment_gallons_monthly as number) ?? null,
    fuel_commitment_gallons_annual: (row.fuel_commitment_gallons_annual as number) ?? null,
    fuel_price_per_gallon: (row.fuel_price_per_gallon as number) ?? null,
    fuel_price_type: (row.fuel_price_type as FuelPriceType) ?? null,
    fuel_price_differential: (row.fuel_price_differential as number) ?? null,
    primary_aircraft_tail: primaryAircraftTail,
    primary_aircraft_type: primaryAircraftType,
    created_at: row.created_at as string,
    updated_at: row.updated_at as string | null,
    deleted_at: row.deleted_at as string | null,
  }
}

/**
 * Fetch all lease contracts with optional filtering
 * Includes joins to clients, contacts, units, locations
 */
export async function fetchLeases(
  organizationId: string,
  filters?: LeaseFilters
): Promise<LeaseTableRow[]> {
  let query = supabase
    .from('lease_contracts')
    .select(LEASE_SELECT)
    .eq('organization_id', organizationId)
    .order('created_at', { ascending: false })

  // Apply record status filter (active/all/deleted)
  query = applyRecordStatusFilter(query, filters?.recordStatus ?? 'active')

  // Apply filters
  if (filters?.status && filters.status.length > 0) {
    query = query.in('status', filters.status)
  }

  if (filters?.type && filters.type.length > 0) {
    query = query.in('type', filters.type)
  }

  if (filters?.locationId) {
    query = query.eq('location_id', filters.locationId)
  }

  if (filters?.search) {
    const search = filters.search.toLowerCase()
    query = query.or(`contract_number.ilike.%${search}%`)
  }

  const { data, error } = await query

  if (error) throw error

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (data ?? []).map((row) => mapToLeaseTableRow(row as any))
}

/**
 * Fetch a single lease by ID with full details
 */
export async function fetchLeaseById(id: string): Promise<LeaseTableRow> {
  const { data, error } = await supabase
    .from('lease_contracts')
    .select(LEASE_SELECT)
    .eq('id', id)
    .single()

  if (error) throw error

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return mapToLeaseTableRow(data as any)
}

/**
 * Fetch aircraft linked to a lease contract
 */
export async function fetchContractAircraft(leaseId: string): Promise<ContractAircraft[]> {
  const { data, error } = await supabase
    .from('contract_aircraft')
    .select(`
      id,
      lease_contract_id,
      aircraft_id,
      is_primary,
      status,
      added_at,
      removed_at,
      aircraft:aircraft_id (
        tail_number,
        aircraft_types:aircraft_type_id (
          manufacturer,
          model
        )
      )
    `)
    .eq('lease_contract_id', leaseId)
    .eq('status', 'active')
    .order('is_primary', { ascending: false })

  if (error) throw error

  return (data ?? []).map((row): ContractAircraft => {
    const aircraft = row.aircraft as unknown as {
      tail_number: string
      aircraft_types: { manufacturer: string; model: string } | null
    } | null

    return {
      id: row.id,
      lease_contract_id: row.lease_contract_id,
      aircraft_id: row.aircraft_id,
      is_primary: row.is_primary ?? false,
      status: row.status as 'active' | 'inactive' | 'removed',
      added_at: row.added_at,
      removed_at: row.removed_at,
      tail_number: aircraft?.tail_number,
      aircraft_type_name: aircraft?.aircraft_types
        ? `${aircraft.aircraft_types.manufacturer} ${aircraft.aircraft_types.model}`
        : null,
    }
  })
}

/**
 * Create a new lease contract with optional aircraft links
 */
export async function createLease(
  organizationId: string,
  data: LeaseFormData,
  aircraftIds?: string[],
  primaryAircraftId?: string | null
): Promise<LeaseTableRow> {
  // Insert the lease (contract_number is auto-generated by trigger)
  const { data: created, error } = await supabase
    .from('lease_contracts')
    .insert([{
      organization_id: organizationId,
      location_id: data.location_id,
      client_id: data.client_id,
      contact_id: data.contact_id ?? null,
      unit_id: data.unit_id,
      type: data.type,
      start_date: data.start_date,
      end_date: data.end_date ?? null,
      term_months: data.term_months ?? null,
      monthly_rate: data.monthly_rate,
      rate_basis: data.rate_basis ?? null,
      security_deposit: data.security_deposit ?? null,
      escalation_type: data.escalation_type ?? null,
      escalation_pct: data.escalation_pct ?? null,
      escalation_date: data.escalation_date ?? null,
      auto_renew: data.auto_renew ?? false,
      renewal_notice_days: data.renewal_notice_days ?? null,
      allow_transient_sublease: data.allow_transient_sublease ?? false,
      transient_revenue_share_pct: data.transient_revenue_share_pct ?? null,
      placement_guarantee: data.placement_guarantee ?? true,
      requires_unblocked_access: data.requires_unblocked_access ?? false,
      status: data.status,
      document_url: data.document_url ?? null,
      signed_at: data.signed_at ?? null,
      notes: data.notes ?? null,
      fuel_type: data.fuel_type ?? null,
      fuel_commitment_gallons_monthly: data.fuel_commitment_gallons_monthly ?? null,
      fuel_commitment_gallons_annual: data.fuel_commitment_gallons_annual ?? null,
      fuel_price_per_gallon: data.fuel_price_per_gallon ?? null,
      fuel_price_type: data.fuel_price_type ?? null,
      fuel_price_differential: data.fuel_price_differential ?? null,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as any])
    .select()
    .single()

  if (error) throw error

  // Insert aircraft links if provided
  if (aircraftIds && aircraftIds.length > 0) {
    const aircraftRecords = aircraftIds.map((aircraftId) => ({
      lease_contract_id: created.id,
      aircraft_id: aircraftId,
      is_primary: aircraftId === primaryAircraftId,
      status: 'active' as const,
    }))

    const { error: aircraftError } = await supabase
      .from('contract_aircraft')
      .insert(aircraftRecords)

    if (aircraftError) throw aircraftError
  }

  // Fetch with full joins for return
  return fetchLeaseById(created.id)
}

/**
 * Update an existing lease contract
 */
export async function updateLease(
  id: string,
  data: Partial<LeaseFormData>,
  aircraftIds?: string[],
  primaryAircraftId?: string | null
): Promise<LeaseTableRow> {
  const updateData: Record<string, unknown> = {}

  // Only include fields that are explicitly provided
  if (data.location_id !== undefined) updateData.location_id = data.location_id
  if (data.client_id !== undefined) updateData.client_id = data.client_id
  if (data.contact_id !== undefined) updateData.contact_id = data.contact_id
  if (data.unit_id !== undefined) updateData.unit_id = data.unit_id
  if (data.type !== undefined) updateData.type = data.type
  if (data.start_date !== undefined) updateData.start_date = data.start_date
  if (data.end_date !== undefined) updateData.end_date = data.end_date
  if (data.term_months !== undefined) updateData.term_months = data.term_months
  if (data.monthly_rate !== undefined) updateData.monthly_rate = data.monthly_rate
  if (data.rate_basis !== undefined) updateData.rate_basis = data.rate_basis
  if (data.security_deposit !== undefined) updateData.security_deposit = data.security_deposit
  if (data.escalation_type !== undefined) updateData.escalation_type = data.escalation_type
  if (data.escalation_pct !== undefined) updateData.escalation_pct = data.escalation_pct
  if (data.escalation_date !== undefined) updateData.escalation_date = data.escalation_date
  if (data.auto_renew !== undefined) updateData.auto_renew = data.auto_renew
  if (data.renewal_notice_days !== undefined) updateData.renewal_notice_days = data.renewal_notice_days
  if (data.allow_transient_sublease !== undefined) updateData.allow_transient_sublease = data.allow_transient_sublease
  if (data.transient_revenue_share_pct !== undefined) updateData.transient_revenue_share_pct = data.transient_revenue_share_pct
  if (data.placement_guarantee !== undefined) updateData.placement_guarantee = data.placement_guarantee
  if (data.requires_unblocked_access !== undefined) updateData.requires_unblocked_access = data.requires_unblocked_access
  if (data.status !== undefined) updateData.status = data.status
  if (data.document_url !== undefined) updateData.document_url = data.document_url
  if (data.signed_at !== undefined) updateData.signed_at = data.signed_at
  if (data.notes !== undefined) updateData.notes = data.notes
  if (data.fuel_type !== undefined) updateData.fuel_type = data.fuel_type
  if (data.fuel_commitment_gallons_monthly !== undefined) updateData.fuel_commitment_gallons_monthly = data.fuel_commitment_gallons_monthly
  if (data.fuel_commitment_gallons_annual !== undefined) updateData.fuel_commitment_gallons_annual = data.fuel_commitment_gallons_annual
  if (data.fuel_price_per_gallon !== undefined) updateData.fuel_price_per_gallon = data.fuel_price_per_gallon
  if (data.fuel_price_type !== undefined) updateData.fuel_price_type = data.fuel_price_type
  if (data.fuel_price_differential !== undefined) updateData.fuel_price_differential = data.fuel_price_differential

  const { error } = await supabase
    .from('lease_contracts')
    .update(updateData)
    .eq('id', id)

  if (error) throw error

  // Update aircraft links if provided
  if (aircraftIds !== undefined) {
    // Mark all existing links as removed
    await supabase
      .from('contract_aircraft')
      .update({ status: 'removed', removed_at: new Date().toISOString() })
      .eq('lease_contract_id', id)
      .eq('status', 'active')

    // Upsert links (re-activates removed rows, inserts new ones)
    if (aircraftIds.length > 0) {
      const aircraftRecords = aircraftIds.map((aircraftId) => ({
        lease_contract_id: id,
        aircraft_id: aircraftId,
        is_primary: aircraftId === primaryAircraftId,
        status: 'active' as const,
        removed_at: null,
      }))

      const { error: aircraftError } = await supabase
        .from('contract_aircraft')
        .upsert(aircraftRecords, {
          onConflict: 'lease_contract_id,aircraft_id',
        })

      if (aircraftError) throw aircraftError
    }
  }

  // Fetch with full joins for return
  return fetchLeaseById(id)
}

/**
 * Soft delete a lease contract
 */
export async function deleteLease(id: string): Promise<void> {
  const { error } = await supabase
    .from('lease_contracts')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', id)

  if (error) throw error
}

/**
 * Restore a soft-deleted lease contract
 */
export async function restoreLease(id: string): Promise<void> {
  return restoreRecord('lease_contracts', id)
}

/**
 * Terminate a lease (early exit / eviction)
 * Sets status to 'terminated' with reason and date
 */
export async function terminateLease(
  id: string,
  params: { termination_reason: string; termination_date: string }
): Promise<void> {
  const { error } = await supabase
    .from('lease_contracts')
    .update({
      status: 'terminated',
      termination_reason: params.termination_reason,
      termination_date: params.termination_date,
    })
    .eq('id', id)

  if (error) throw error
}

/**
 * Cancel a lease (draft / pending abandoned)
 * Sets status to 'cancelled' with optional reason
 */
export async function cancelLease(
  id: string,
  params?: { cancellation_reason?: string }
): Promise<void> {
  const { error } = await supabase
    .from('lease_contracts')
    .update({
      status: 'cancelled',
      cancellation_reason: params?.cancellation_reason ?? null,
    })
    .eq('id', id)

  if (error) throw error
}

/**
 * Complete a lease (happy-path end of term)
 * Sets status to 'completed'
 */
export async function completeLease(id: string): Promise<void> {
  const { error } = await supabase
    .from('lease_contracts')
    .update({ status: 'completed' })
    .eq('id', id)

  if (error) throw error
}
